// components/DropoffPrecision.js
// ─────────────────────────────────────────────────────────────────────────────
// "How good is the drop-off pin?" — one pill for every order surface.
//
//   <DropoffPrecision shippingAddress={order.shippingAddress} />
//
// The delivery fee is measured to shippingAddress.latLng, so whoever looks at
// an order — dispatcher, vendor, support handling a fee dispute — needs to
// know how that pin was set. The apps stamp two fields at checkout:
//
//   source        "gps"     the customer's live device fix (preciseLocation.js)
//                 "places"  an address picked from search; the pin is the
//                           geocoder's point for that place, not the gate
//   gpsAccuracyM  for gps: the OS 68% radius of the fused fix, in metres
//
// Bands mirror the app's GPS_* constants: ≤15 m precise, ≤30 m good,
// ≤100 m approximate (the customer accepted it explicitly), wider = rough.
// Renders nothing when the order predates the fields, so call sites can pass
// any shippingAddress without guarding.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";

const TONES = {
  precise: { dot: "#2e7d32", bg: "#e8f5e9", border: "#c8e6c9", text: "#1b5e20" },
  good: { dot: "#2e7d32", bg: "#e8f5e9", border: "#c8e6c9", text: "#1b5e20" },
  approximate: { dot: "#ef6c00", bg: "#fff3e0", border: "#ffe0b2", text: "#8a4b00" },
  rough: { dot: "#c62828", bg: "#fdecea", border: "#f5c6cb", text: "#8c1c13" },
  places: { dot: "#2c7be5", bg: "#eaf2ff", border: "#cfe0fb", text: "#1a4f9c" },
};

/**
 * Pure describer — also used by tables/tooltips that want text only.
 * @returns {{ tone, label, detail } | null}
 */
export const describeDropoffPrecision = (shippingAddress) => {
  const source = shippingAddress?.source;
  if (source === "gps") {
    const m = Number(shippingAddress?.gpsAccuracyM);
    if (!Number.isFinite(m) || m <= 0) {
      return { tone: "approximate", label: "GPS pin", detail: "Accuracy not recorded" };
    }
    const r = Math.round(m);
    const tone = m <= 15 ? "precise" : m <= 30 ? "good" : m <= 100 ? "approximate" : "rough";
    const detail =
      tone === "precise" || tone === "good"
        ? "Live device fix — the pin is at the customer"
        : tone === "approximate"
        ? "Customer accepted an approximate fix — rider should confirm the gate"
        : "Rough fix — treat the pin as the neighbourhood, call before dispatch";
    return { tone, label: `GPS pin · accurate to ±${r} m`, detail, metres: r };
  }
  if (source === "places") {
    return {
      tone: "places",
      label: "Address-search pin",
      detail: "Geocoder point for the address, not the exact gate — confirm with the customer",
    };
  }
  return null;
};

const DropoffPrecision = ({ shippingAddress, style, compact = false }) => {
  const d = describeDropoffPrecision(shippingAddress);
  if (!d) return null;
  const t = TONES[d.tone];
  return (
    <span
      title={d.detail}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compact ? "1px 8px" : "3px 10px",
        borderRadius: 999,
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        ...style,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flex: "0 0 auto" }} />
      {d.label}
    </span>
  );
};

export default DropoffPrecision;
